import type {AboutJourneyType} from '@cohort/components-xps/components/journeys/AboutJourney';
import AboutJourney from '@cohort/components-xps/components/journeys/AboutJourney';
import type {App} from '@cohort/wallet/apps';
import {getAppFromTriggerIntegration} from '@cohort/wallet/apps/TriggerIntegration';
import {useVerifyStep} from '@cohort/wallet/apps/useStepVerification';
import JourneyActionBtn from '@cohort/wallet/components/space/JourneyActionButton';
import JourneyStepsProgression from '@cohort/wallet/components/space/JourneyStepsProgression';
import {useJourneyCampaign} from '@cohort/wallet/hooks/api/Campaign';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {useTriggerIntegrations} from '@cohort/wallet/hooks/useTriggerIntegrations';
import SpacePage from '@cohort/wallet/layouts/space/SpacePage';
import {getJourneyDetailRoute} from '@cohort/wallet/lib/Pages';
import type {CampaignJourneyWDto} from '@cohort/wallet-schemas/campaign';
import type {JourneyStepTriggerWDto} from '@cohort/wallet-schemas/journeySteps';
import type {VerificationAttemptInput} from '@cohort/wallet-schemas/journeyStepTriggers';
import {Fragment} from 'react';
import {Navigate, useNavigate, useParams} from 'react-router-dom';

type TriggerUsagePageContentProps = {
  trigger: JourneyStepTriggerWDto;
  app: App;
  campaign: CampaignJourneyWDto;
  journeyId: string;
  stepId: string;
  merchantId: string;
};

const TriggerUsagePageContent: React.FC<TriggerUsagePageContentProps> = ({
  app,
  campaign,
  journeyId,
  merchantId,
  stepId,
  trigger,
}) => {
  const navigate = useNavigate();
  const {setTransitioning} = useSpaceContext();
  const {backgroundColor, hasDarkBg} = useThemeContext();
  const journey = campaign.journey;
  const {getTriggerIntegration} = useTriggerIntegrations();
  const triggerIntegration = getTriggerIntegration(trigger.triggerIntegrationId);

  const {processStepVerification} = useVerifyStep(
    trigger,
    app,
    journeyId,
    campaign.journey.participation?.id,
    stepId,
    merchantId
  );

  const handleUsageSuccess = (verificationAttemptInput: VerificationAttemptInput): void => {
    processStepVerification(verificationAttemptInput);
    setTransitioning({active: true, enterFrom: 'right'});
    navigate(getJourneyDetailRoute(journeyId), {
      replace: true,
      state: {processingStepId: stepId},
    });
  };

  const aboutJourney = {
    animationFileKey: journey.animationFileKey,
    backgroundColorCode: backgroundColor,
    availableSupply: campaign.availableSupply,
    description: journey.description,
    hasDarkBg,
    imageFileKey: journey.imageFileKey,
    title: journey.title,
    isCampaignEnded: campaign.status === 'ended',
  } satisfies AboutJourneyType;

  const AboutJourneyComponent = (
    <AboutJourney aboutJourney={aboutJourney}>
      <Fragment>
        {campaign.journey.participation !== null && (
          <JourneyStepsProgression steps={campaign.journey.steps} />
        )}
        <JourneyActionBtn campaign={campaign} />
      </Fragment>
    </AboutJourney>
  );

  if (triggerIntegration.usageComponent === null) {
    throw new Error(`No usageComponent found for triggerIntegrationId: ${trigger.id}`);
  }

  return (
    <SpacePage
      title={journey.title}
      tracking={{
        namespace: 'journeys.detail.triggerUsage',
        metadata: {
          journeyId,
          stepId,
          triggerId: trigger.id,
        },
      }}
      leftPanel={AboutJourneyComponent}
    >
      <triggerIntegration.usageComponent
        onTriggerIntegrationUsageSuccess={handleUsageSuccess}
        config={trigger.triggerIntegrationConfig}
      />
    </SpacePage>
  );
};

const TriggerUsagePage: React.FC = () => {
  const {space} = useSpaceContext();
  const {journeyId, stepId, triggerId} = useParams();

  const {data: campaign, isFetched: isCampaignFetched} = useJourneyCampaign(
    space?.merchant.id ?? '',
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    journeyId!,
    space !== undefined && journeyId !== undefined
  );

  if (triggerId === undefined || journeyId === undefined || stepId === undefined) {
    return <Navigate to="/space/not-found" />;
  }

  const journey = campaign?.journey;
  const currentStep = journey?.steps.find(step => step.id === stepId);
  const currentTrigger = currentStep?.triggers.find(trigger => trigger.id === triggerId);
  const currentApp = currentTrigger?.triggerIntegrationId
    ? getAppFromTriggerIntegration(currentTrigger.triggerIntegrationId)
    : undefined;

  if (isCampaignFetched && campaign === undefined) {
    return <Navigate to="/space/not-found" />;
  } else if (space === undefined || campaign === undefined || journey === undefined) {
    return <Fragment />;
  } else if (currentStep === undefined) {
    throw new Error(`No step found for id: ${stepId}`);
  } else if (currentTrigger === undefined) {
    throw new Error(`No trigger found for id: ${triggerId}`);
  } else if (currentApp === undefined) {
    throw new Error(
      `No app found for triggerIntegrationId: ${currentTrigger.triggerIntegrationId}`
    );
  }

  return (
    <TriggerUsagePageContent
      app={currentApp}
      campaign={campaign}
      journeyId={journeyId}
      merchantId={space.merchant.id}
      stepId={currentStep.id}
      trigger={currentTrigger}
    />
  );
};

export default TriggerUsagePage;
